import React, { ChangeEventHandler, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { appActions } from '../../state/modules/app';
import AuthError from '../../components/AuthError';
import { Link } from 'react-router-dom';
import btnStyles from '../../styles/partials/_button.css';
import componentStyles from '../../styles/partials/_login.css';
import CurrentUserQuery from '../../graphql/queries/system/CurrentUserQuery.graphql';
import setDocumentTitle from '../../utils/SetDocumentTitle';
import AboutModal from '../../components/modals/AboutModal';
import { serviceActions } from '../../state/modules/service';
import { AlertType, ICurrentUserQueryResult, ICurrentUserQueryVariables } from '../../graphql/schema/p4types';
import { useQuery } from '@apollo/client';
import { useHistory, useParams } from 'react-router';
import { p4LightTheme } from '../../styles/themes/default/p4FluentTheme';
import { FluentProvider } from '@fluentui/react-components';
import { ApplyToBody } from './ApplyToBody';

const styles = Object.assign({}, componentStyles, btnStyles);

const Login = () => {
  const params = useParams<{ username?: string }>();
  setDocumentTitle('Login');
  const usernameParam = params.username;
  const [username, setUserName] = useState(usernameParam && usernameParam !== 'Auth' ? usernameParam : '');
  const [password, setPassword] = useState('');
  const [capsLock, setCapsLock] = useState(false);
  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const dispatch = useDispatch();

  const history = useHistory();

  const {
    data: user,
    loading: userLoading,
    error: userError,
  } = useQuery<ICurrentUserQueryResult, ICurrentUserQueryVariables>(CurrentUserQuery, {
    fetchPolicy: 'network-only',
  });

  const usernameRef = useRef<any>();
  const passwordRef = useRef<any>();
  const submitRef = useRef<any>();

  //didMount
  useEffect(() => {
    if (username) {
      passwordRef.current.focus();
    } else {
      usernameRef.current.focus();
    }
    //Do NOT add 'username' as a dependency - Only if it's populated on initial load should this focus occur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //TODO - 10.5+ - Use 'IReduxStore' type.
  const isLoggedIn = useSelector((store: any) => store.app.isLoggedIn);
  const authAlert = useSelector((store: any) => store.app.authAlert);

  //didUpdate
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const destination = params.has('dest') ? params.get('dest')! : '/';

    if (isLoggedIn || (user?.currentUser && !userLoading && !userError)) {
      history.replace(destination);
    }

    submitRef.current?.removeAttribute?.('disabled');
  }, [history, user, submitRef, isLoggedIn, userLoading, userError]);

  const handleUserChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    dispatch(appActions.dismissAuthAlert());
    setUserName(event.target.value);
  };

  const handlePasswordChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    dispatch(appActions.dismissAuthAlert());
    setPassword(event.target.value);
  };

  const checkCapsLock: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') return;

    if (/[a-zA-Z]/.test(event.key) && event.key === event.key.toUpperCase() && !event.shiftKey) {
      setCapsLock(true);
    } else {
      setCapsLock(false);
    }
  };

  let capsLockWarn = null;
  let authMessage = null;

  if (authAlert) {
    authMessage = authAlert.message;
  }

  if (capsLock) {
    capsLockWarn = <AuthError message="Caps lock may be on" />;
    authMessage += ' (Caps lock may be on)';
  }

  const aboutModal = <AboutModal isOpen={aboutModalOpen} requestClose={() => setAboutModalOpen(false)} />;

  const handleLogin: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (!username) {
      dispatch(appActions.userFailure('Username must be provided'));
      return;
    }
    if (!password) {
      dispatch(appActions.userFailure('Password must be provided'));
      return;
    }
    submitRef.current?.blur?.();
    submitRef.current?.setAttribute?.('disabled', true);

    const userAgent = navigator?.userAgent ?? 'Unknown';

    const clientInformation = {
      userAgent: userAgent,
      height: screen ? screen.height : null,
      width: screen ? screen.width : null,
      os: (window.navigator as any).oscpu || window.navigator.platform,
    };

    const result = await dispatch(appActions.loginRequest(username, password, clientInformation));

    if (result?.type === 'app/USER_FAILURE') {
      if (submitRef?.current) {
        submitRef.current.disabled = false;
      }
    }

    const isFirefox = userAgent.indexOf('Firefox') !== -1;
    const isChrome = userAgent.indexOf('Chrome') !== -1;
    const isSafari = userAgent.indexOf('Safari') !== -1;

    //On login, advise user if they're using an incompatible browser..
    if (!isFirefox && !isChrome && !isSafari) {
      //TODO - On 10.5+ - Migrate to using toast system + fix timeout (To ~10 seconds) rather than using default info timer.
      dispatch(
        serviceActions.genericAlert(
          "It looks like you're using an incompatible browser!",
          `Profit4 makes use of modern technologies that aren't supported by your browser.\nPlease re-open the application in a recent version of Chrome, Firefox or Edge\n\n Your user agent is ${userAgent}`,
          AlertType.Info
        )
      );
    }
  };

  return (
    <FluentProvider theme={p4LightTheme}>
      <ApplyToBody />
      <div>
        <div />
        <div className={styles.container}>
          {capsLockWarn}
          {authAlert && authAlert.type !== 'session' ? <AuthError message={authMessage} /> : ''}
          <div className={styles.loginDim} />
          <div className={styles.loginSection}>
            <div className={styles.loginBoxes}>
              <div className={styles.loginTealBox} />
              <div className={styles.loginIndigoBox} />
            </div>
            <div className={styles.loginLogo}>
              <img className={styles.loginLogoImg} src="/images/OGL-Profit4Logo.png" alt="OGL Profit4 Logo" />
            </div>
            <div className={styles.loginForm}>
              <div
                style={{
                  float: 'right',
                  color: '#bdbdbd',
                  cursor: 'pointer',
                  margin: '18px 5px 0 5px',
                }}
                onClick={(e) => {
                  setAboutModalOpen(true);
                }}
              >
                <i style={{ fontSize: '22px' }} className="icon">
                  info_outline
                </i>
              </div>
              <form onSubmit={handleLogin}>
                <h3 className={styles.heading}>
                  <span className={styles.headingAlt}>login</span>
                </h3>
                <input
                  type="text"
                  ref={usernameRef}
                  onChange={handleUserChange}
                  value={username}
                  className={styles.input}
                  aria-describedby="sizing-addon1"
                  autoFocus={true}
                  placeholder="Username"
                  data-testid="login-user"
                />
                <input
                  type="password"
                  ref={passwordRef}
                  onChange={handlePasswordChange}
                  onKeyPress={checkCapsLock}
                  value={password}
                  className={styles.input}
                  aria-describedby="sizing-addon1"
                  placeholder="Password"
                  data-testid="login-password"
                />
                <button ref={submitRef} type="submit" data-testid="login-submit" className={styles.loginBtn}>
                  <i
                    style={{
                      paddingRight: `${5}px`,
                      lineHeight: 'inherit',
                      verticalAlign: 'bottom',
                    }}
                    className="iconSmall"
                    aria-hidden="true"
                  >
                    lock
                  </i>
                  Login
                </button>
                <Link to="/RequestPasswordReset" className={styles.forgotten}>
                  Forgot Password?
                </Link>
              </form>
            </div>
            <img className={styles.loginOGLImg} src="/images/OGL_Software_Logo.png" alt="OGL Software Logo" />
          </div>
        </div>
        {aboutModal}
      </div>
    </FluentProvider>
  );
};

export default Login;
